import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const getDateObject = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const getTimestamp = (value) =>
  getDateObject(value)?.getTime() || 0;

const formatDateTime = (value) => {
  const date = getDateObject(value);

  if (!date) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeRoom = (value) =>
  clean(value).toUpperCase();

const STATUS_OPTIONS = [
  {
    value: "open",
    label: "Open",
  },
  {
    value: "assigned",
    label: "Assigned",
  },
  {
    value: "in_progress",
    label: "In Progress",
  },
  {
    value: "waiting_parts",
    label: "Waiting for Parts",
  },
  {
    value: "resolved",
    label: "Resolved",
  },
  {
    value: "closed",
    label: "Closed",
  },
];

const PRIORITY_OPTIONS = [
  "Low",
  "Normal",
  "High",
  "Urgent",
];

const CATEGORY_OPTIONS = [
  "Electrical",
  "Plumbing",
  "AC",
  "TV",
  "Geyser",
  "Furniture",
  "Bathroom",
  "Door / Lock",
  "Internet",
  "Painting",
  "Other",
];

const getStatusLabel = (status) =>
  STATUS_OPTIONS.find(
    (item) => item.value === status
  )?.label || "Open";

const getTicketSearchText = (ticket) =>
  [
    ticket.room,
    ticket.category,
    ticket.priority,
    ticket.description,
    ticket.status,
    ticket.assignedTo,
    ticket.guestName,
    ticket.reportedBy,
    ticket.maintenanceTicketId,
    ticket.id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/* -------------------------------------------------------------------------- */
/*                             Maintenance Panel                              */
/* -------------------------------------------------------------------------- */

function MaintenancePanel({
  hotelId,
  hotelName,

  onSendRoomToCleaning,
}) {
  const [tickets, setTickets] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("active");

  const [
    priorityFilter,
    setPriorityFilter,
  ] = useState("all");

  const [
    categoryFilter,
    setCategoryFilter,
  ] = useState("all");

  const [sortBy, setSortBy] =
    useState("recent");

  const [
    selectedTicket,
    setSelectedTicket,
  ] = useState(null);

  const [saving, setSaving] =
    useState(false);

  /* ------------------------------------------------------------------------ */
  /*                           Firestore Listener                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId) {
      setTickets([]);
      setLoading(false);
      setLoadError(
        "Hotel ID missing hai."
      );

      return undefined;
    }

    setLoading(true);
    setLoadError("");

    const ticketsQuery = query(
      collection(
        db,
        "maintenanceTickets"
      ),
      where(
        "hotelId",
        "==",
        hotelId
      )
    );

    return onSnapshot(
      ticketsQuery,

      (snapshot) => {
        const list = snapshot.docs
          .map((ticketDocument) => ({
            id: ticketDocument.id,
            ...ticketDocument.data(),
          }))
          .sort(
            (firstTicket, secondTicket) =>
              getTimestamp(
                secondTicket.reportedAt ||
                  secondTicket.createdAt
              ) -
              getTimestamp(
                firstTicket.reportedAt ||
                  firstTicket.createdAt
              )
          );

        setTickets(list);
        setLoading(false);
        setLoadError("");
      },

      (error) => {
        console.error(
          "Maintenance tickets load error:",
          error
        );

        setTickets([]);
        setLoading(false);

        setLoadError(
          error?.message ||
            "Maintenance tickets load nahi hue."
        );
      }
    );
  }, [hotelId]);

  /* ------------------------------------------------------------------------ */
  /*                              Summary                                      */
  /* ------------------------------------------------------------------------ */

  const summary = useMemo(() => {
    const activeTickets =
      tickets.filter(
        (ticket) =>
          ![
            "resolved",
            "closed",
          ].includes(ticket.status)
      );

    return {
      total: tickets.length,

      open: tickets.filter(
        (ticket) =>
          ticket.status === "open"
      ).length,

      assigned: tickets.filter(
        (ticket) =>
          ticket.status === "assigned"
      ).length,

      inProgress: tickets.filter(
        (ticket) =>
          ticket.status ===
          "in_progress"
      ).length,

      waitingParts: tickets.filter(
        (ticket) =>
          ticket.status ===
          "waiting_parts"
      ).length,

      urgent: activeTickets.filter(
        (ticket) =>
          ticket.priority === "Urgent"
      ).length,

      resolved: tickets.filter(
        (ticket) =>
          ticket.status === "resolved"
      ).length,

      active: activeTickets.length,
    };
  }, [tickets]);

  /* ------------------------------------------------------------------------ */
  /*                          Filter and Sorting                               */
  /* ------------------------------------------------------------------------ */

  const filteredTickets = useMemo(() => {
    const searchTerm =
      lower(search);

    const filtered =
      tickets.filter((ticket) => {
        const matchesSearch =
          !searchTerm ||
          getTicketSearchText(
            ticket
          ).includes(searchTerm);

        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "active" &&
            ![
              "resolved",
              "closed",
            ].includes(
              ticket.status
            )) ||
          ticket.status ===
            statusFilter;

        const matchesPriority =
          priorityFilter === "all" ||
          ticket.priority ===
            priorityFilter;

        const matchesCategory =
          categoryFilter === "all" ||
          ticket.category ===
            categoryFilter;

        return (
          matchesSearch &&
          matchesStatus &&
          matchesPriority &&
          matchesCategory
        );
      });

    return [...filtered].sort(
      (firstTicket, secondTicket) => {
        if (sortBy === "priority") {
          const priorityOrder = {
            Urgent: 4,
            High: 3,
            Normal: 2,
            Low: 1,
          };

          return (
            (priorityOrder[
              secondTicket.priority
            ] || 0) -
            (priorityOrder[
              firstTicket.priority
            ] || 0)
          );
        }

        if (sortBy === "room") {
          return normalizeRoom(
            firstTicket.room
          ).localeCompare(
            normalizeRoom(
              secondTicket.room
            ),
            undefined,
            {
              numeric: true,
            }
          );
        }

        if (sortBy === "oldest") {
          return (
            getTimestamp(
              firstTicket.reportedAt ||
                firstTicket.createdAt
            ) -
            getTimestamp(
              secondTicket.reportedAt ||
                secondTicket.createdAt
            )
          );
        }

        return (
          getTimestamp(
            secondTicket.reportedAt ||
              secondTicket.createdAt
          ) -
          getTimestamp(
            firstTicket.reportedAt ||
              firstTicket.createdAt
          )
        );
      }
    );
  }, [
    tickets,
    search,
    statusFilter,
    priorityFilter,
    categoryFilter,
    sortBy,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                            Update Ticket                                  */
  /* ------------------------------------------------------------------------ */

  const updateTicket = async (
    ticketId,
    updates
  ) => {
    if (!ticketId || saving) {
      return;
    }

    setSaving(true);

    try {
      await updateDoc(
        doc(
          db,
          "maintenanceTickets",
          ticketId
        ),
        {
          ...updates,

          updatedAt:
            serverTimestamp(),
        }
      );

      setSelectedTicket(
        (current) =>
          current?.id === ticketId
            ? {
                ...current,
                ...updates,
              }
            : current
      );
    } catch (error) {
      console.error(
        "Maintenance ticket update error:",
        error
      );

      alert(
        "Maintenance ticket update nahi hua: " +
          (error?.message || error)
      );

      throw error;
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                           Assign Technician                               */
  /* ------------------------------------------------------------------------ */

  const assignTechnician =
    async (ticket, assignedTo) => {
      const technician =
        clean(assignedTo);

      if (!technician) {
        alert(
          "Technician ya staff ka naam enter karo."
        );

        return;
      }

      await updateTicket(
        ticket.id,
        {
          assignedTo: technician,

          assignedAt:
            new Date().toISOString(),

          status:
            ticket.status === "open"
              ? "assigned"
              : ticket.status,
        }
      );
    };

  /* ------------------------------------------------------------------------ */
  /*                            Start Work                                     */
  /* ------------------------------------------------------------------------ */

  const startWork = async (
    ticket
  ) => {
    if (!clean(ticket.assignedTo)) {
      alert(
        "Kaam start karne se pehle technician assign karo."
      );

      return;
    }

    await updateTicket(
      ticket.id,
      {
        status: "in_progress",

        workStartedAt:
          ticket.workStartedAt ||
          new Date().toISOString(),
      }
    );
  };

  /* ------------------------------------------------------------------------ */
  /*                          Mark Waiting Parts                               */
  /* ------------------------------------------------------------------------ */

  const markWaitingParts =
    async (ticket, note) => {
      const waitingNote =
        clean(note);

      if (!waitingNote) {
        alert(
          "Required part ya delay note enter karo."
        );

        return;
      }

      await updateTicket(
        ticket.id,
        {
          status: "waiting_parts",

          waitingPartsNote:
            waitingNote,

          waitingPartsAt:
            new Date().toISOString(),
        }
      );
    };

  /* ------------------------------------------------------------------------ */
  /*                              Resolve Ticket                               */
  /* ------------------------------------------------------------------------ */

  const resolveTicket = async (
    ticket,
    resolution
  ) => {
    const resolutionNote =
      clean(resolution);

    if (!resolutionNote) {
      alert(
        "Resolution note required hai."
      );

      return;
    }

    const confirmed =
      window.confirm(
        `Room ${ticket.room || "-"} ka maintenance issue resolved mark karna hai?`
      );

    if (!confirmed) return;

    const resolved = {...ticket, status: 'resolved', resolutionNote, resolvedAt: new Date().toISOString(), maintenanceCompleted: true};
    if (typeof onSendRoomToCleaning === 'function') {
      await onSendRoomToCleaning(resolved); // Parent commits task, lock, board and ticket together.
    } else {
      await updateTicket(ticket.id, resolved);
    }

  };

  /* ------------------------------------------------------------------------ */
  /*                              Close Ticket                                 */
  /* ------------------------------------------------------------------------ */

  const closeTicket = async (
    ticket
  ) => {
    if (
      ticket.status !== "resolved"
    ) {
      alert(
        "Ticket close karne se pehle resolved mark karo."
      );

      return;
    }

    const confirmed =
      window.confirm(
        "Maintenance ticket permanently close karna hai?"
      );

    if (!confirmed) return;

    await updateTicket(
      ticket.id,
      {
        status: "closed",

        closedAt:
          new Date().toISOString(),
      }
    );

    setSelectedTicket(null);
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <section style={sectionStyle}>
      <div style={titleRowStyle}>
        <div>
          <h3 style={titleStyle}>
            🔧 Maintenance Panel
          </h3>

          <div style={subtitleStyle}>
            {hotelName || hotelId} ke
            room maintenance issues aur
            repair tracking
          </div>
        </div>

        <div style={activeBadgeStyle}>
          {summary.active} Active
        </div>
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Total Tickets"
          value={summary.total}
        />

        <SummaryCard
          label="Open"
          value={summary.open}
          warning={summary.open > 0}
        />

        <SummaryCard
          label="Assigned"
          value={summary.assigned}
        />

        <SummaryCard
          label="In Progress"
          value={summary.inProgress}
        />

        <SummaryCard
          label="Waiting Parts"
          value={summary.waitingParts}
          warning={
            summary.waitingParts > 0
          }
        />

        <SummaryCard
          label="Urgent"
          value={summary.urgent}
          danger={summary.urgent > 0}
        />

        <SummaryCard
          label="Resolved"
          value={summary.resolved}
        />
      </div>

      <div style={toolbarStyle}>
        <input
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder="Room, problem, category, technician..."
          style={inputStyle}
        />

        <select
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="active">
            Active Tickets
          </option>

          <option value="all">
            All Tickets
          </option>

          {STATUS_OPTIONS.map(
            (option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            )
          )}
        </select>

        <select
          value={priorityFilter}
          onChange={(event) =>
            setPriorityFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="all">
            All Priorities
          </option>

          {PRIORITY_OPTIONS.map(
            (priority) => (
              <option
                key={priority}
                value={priority}
              >
                {priority}
              </option>
            )
          )}
        </select>

        <select
          value={categoryFilter}
          onChange={(event) =>
            setCategoryFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="all">
            All Categories
          </option>

          {CATEGORY_OPTIONS.map(
            (category) => (
              <option
                key={category}
                value={category}
              >
                {category}
              </option>
            )
          )}
        </select>

        <select
          value={sortBy}
          onChange={(event) =>
            setSortBy(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="recent">
            Recent First
          </option>

          <option value="oldest">
            Oldest First
          </option>

          <option value="priority">
            Priority First
          </option>

          <option value="room">
            Room Number
          </option>
        </select>
      </div>

      {loadError && (
        <div style={errorBoxStyle}>
          {loadError}
        </div>
      )}

      {loading ? (
        <div style={emptyBoxStyle}>
          Maintenance tickets loading...
        </div>
      ) : filteredTickets.length ===
        0 ? (
        <div style={emptyBoxStyle}>
          Matching maintenance ticket
          nahi mila.
        </div>
      ) : (
        <div style={ticketGridStyle}>
          {filteredTickets.map(
            (ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() =>
                  setSelectedTicket(
                    ticket
                  )
                }
                style={{
                  ...ticketCardStyle,

                  borderColor:
                    ticket.priority ===
                    "Urgent"
                      ? "#fca5a5"
                      : ticket.priority ===
                        "High"
                      ? "#fdba74"
                      : "#e2e8f0",

                  background:
                    ticket.priority ===
                    "Urgent"
                      ? "#fff7f7"
                      : "#ffffff",
                }}
              >
                <div style={ticketTopStyle}>
                  <div>
                    <div style={roomTitleStyle}>
                      Room{" "}
                      {ticket.room || "-"}
                    </div>

                    <div style={categoryStyle}>
                      {ticket.category ||
                        "Other"}
                    </div>
                  </div>

                  <div style={badgeColumnStyle}>
                    <PriorityBadge
                      priority={
                        ticket.priority
                      }
                    />

                    <StatusBadge
                      status={ticket.status}
                    />
                  </div>
                </div>

                <div
                  style={descriptionStyle}
                >
                  {ticket.description ||
                    "No description"}
                </div>

                <div style={ticketMetaGridStyle}>
                  <MetaItem
                    label="Reported"
                    value={formatDateTime(
                      ticket.reportedAt ||
                        ticket.createdAt
                    )}
                  />

                  <MetaItem
                    label="Assigned To"
                    value={
                      ticket.assignedTo ||
                      "Not Assigned"
                    }
                  />
                </div>
              </button>
            )
          )}
        </div>
      )}

      {selectedTicket && (
        <MaintenanceTicketModal
          ticket={selectedTicket}
          saving={saving}
          onClose={() =>
            setSelectedTicket(null)
          }
          onAssign={
            assignTechnician
          }
          onStart={startWork}
          onWaitingParts={
            markWaitingParts
          }
          onResolve={
            resolveTicket
          }
          onCloseTicket={
            closeTicket
          }
          onUpdate={updateTicket}
        />
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                         Maintenance Ticket Modal                           */
/* -------------------------------------------------------------------------- */

function MaintenanceTicketModal({
  ticket,
  saving,

  onClose,
  onAssign,
  onStart,
  onWaitingParts,
  onResolve,
  onCloseTicket,
  onUpdate,
}) {
  const [form, setForm] = useState({
    assignedTo:
      ticket.assignedTo || "",

    category:
      ticket.category || "Other",

    priority:
      ticket.priority || "Normal",

    description:
      ticket.description || "",

    internalNote:
      ticket.internalNote || "",

    waitingPartsNote:
      ticket.waitingPartsNote || "",

    resolutionNote:
      ticket.resolutionNote || "",
  });

  useEffect(() => {
    setForm({
      assignedTo:
        ticket.assignedTo || "",

      category:
        ticket.category || "Other",

      priority:
        ticket.priority || "Normal",

      description:
        ticket.description || "",

      internalNote:
        ticket.internalNote || "",

      waitingPartsNote:
        ticket.waitingPartsNote || "",

      resolutionNote:
        ticket.resolutionNote || "",
    });
  }, [ticket]);

  const saveDetails = async () => {
    if (!clean(form.description)) {
      alert(
        "Problem description required hai."
      );

      return;
    }

    await onUpdate(
      ticket.id,
      {
        category: form.category,

        priority: form.priority,

        description: clean(
          form.description
        ),

        internalNote: clean(
          form.internalNote
        ),
      }
    );

    alert(
      "Maintenance details save ho gayi ✅"
    );
  };

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
          event.currentTarget &&
          !saving
        ) {
          onClose?.();
        }
      }}
    >
      <div style={modalStyle}>
        <header style={modalHeaderStyle}>
          <div>
            <div style={modalTitleRowStyle}>
              <h2 style={modalTitleStyle}>
                Room {ticket.room || "-"}
              </h2>

              <PriorityBadge
                priority={ticket.priority}
              />

              <StatusBadge
                status={ticket.status}
              />
            </div>

            <div style={subtitleStyle}>
              Ticket ID: {ticket.id}
            </div>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={() =>
              onClose?.()
            }
            style={closeButtonStyle}
          >
            ×
          </button>
        </header>

        <div style={infoGridStyle}>
          <InfoBox
            label="Reported At"
            value={formatDateTime(
              ticket.reportedAt ||
                ticket.createdAt
            )}
          />

          <InfoBox
            label="Guest"
            value={
              ticket.guestName || "-"
            }
          />

          <InfoBox
            label="Stay ID"
            value={ticket.stayId || "-"}
          />

          <InfoBox
            label="Source"
            value={
              ticket.reportedFrom ||
              "Manual"
            }
          />
        </div>

        <section style={modalSectionStyle}>
          <h3 style={modalSectionTitleStyle}>
            Issue Details
          </h3>

          <div style={formGridStyle}>
            <Field label="Category">
              <select
                value={form.category}
                disabled={saving}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    category:
                      event.target.value,
                  }))
                }
                style={inputStyle}
              >
                {CATEGORY_OPTIONS.map(
                  (category) => (
                    <option
                      key={category}
                      value={category}
                    >
                      {category}
                    </option>
                  )
                )}
              </select>
            </Field>

            <Field label="Priority">
              <select
                value={form.priority}
                disabled={saving}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    priority:
                      event.target.value,
                  }))
                }
                style={inputStyle}
              >
                {PRIORITY_OPTIONS.map(
                  (priority) => (
                    <option
                      key={priority}
                      value={priority}
                    >
                      {priority}
                    </option>
                  )
                )}
              </select>
            </Field>
          </div>

          <Field label="Problem Description">
            <textarea
              value={form.description}
              disabled={saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  description:
                    event.target.value,
                }))
              }
              style={{
                ...inputStyle,
                minHeight: 90,
                resize: "vertical",
              }}
            />
          </Field>

          <Field label="Internal Note">
            <textarea
              value={form.internalNote}
              disabled={saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  internalNote:
                    event.target.value,
                }))
              }
              style={{
                ...inputStyle,
                minHeight: 75,
                resize: "vertical",
              }}
              placeholder="Manager note, vendor details..."
            />
          </Field>

          <button
            type="button"
            disabled={saving}
            onClick={saveDetails}
            style={saveButtonStyle}
          >
            Save Ticket Details
          </button>
        </section>

        <section style={modalSectionStyle}>
          <h3 style={modalSectionTitleStyle}>
            Technician Assignment
          </h3>

          <div style={inlineFormStyle}>
            <input
              value={form.assignedTo}
              disabled={saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  assignedTo:
                    event.target.value,
                }))
              }
              style={{
                ...inputStyle,
                flex: 1,
              }}
              placeholder="Technician / staff name"
            />

            <button
              type="button"
              disabled={saving}
              onClick={() =>
                onAssign(
                  ticket,
                  form.assignedTo
                )
              }
              style={assignButtonStyle}
            >
              Assign
            </button>
          </div>

          {ticket.assignedAt && (
            <div style={smallInfoStyle}>
              Assigned At:{" "}
              {formatDateTime(
                ticket.assignedAt
              )}
            </div>
          )}
        </section>

        <section style={modalSectionStyle}>
          <h3 style={modalSectionTitleStyle}>
            Work Status
          </h3>

          <div style={actionGridStyle}>
            <button
              type="button"
              disabled={saving}
              onClick={() =>
                onStart(ticket)
              }
              style={startButtonStyle}
            >
              ▶ Start Work
            </button>

            <button
              type="button"
              disabled={saving}
              onClick={() =>
                onWaitingParts(
                  ticket,
                  form.waitingPartsNote
                )
              }
              style={waitingButtonStyle}
            >
              ⏳ Waiting for Parts
            </button>
          </div>

          <Field label="Parts / Delay Note">
            <textarea
              value={
                form.waitingPartsNote
              }
              disabled={saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  waitingPartsNote:
                    event.target.value,
                }))
              }
              style={{
                ...inputStyle,
                minHeight: 70,
                resize: "vertical",
              }}
              placeholder="Required spare part, vendor delay..."
            />
          </Field>
        </section>

        <section style={resolutionSectionStyle}>
          <h3 style={modalSectionTitleStyle}>
            Resolve Issue
          </h3>

          <Field label="Resolution Note">
            <textarea
              value={form.resolutionNote}
              disabled={saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  resolutionNote:
                    event.target.value,
                }))
              }
              style={{
                ...inputStyle,
                minHeight: 80,
                resize: "vertical",
              }}
              placeholder="Repair work completed, part replaced..."
            />
          </Field>

          <button
            type="button"
            disabled={
              saving ||
              ticket.status === "closed"
            }
            onClick={() =>
              onResolve(
                ticket,
                form.resolutionNote
              )
            }
            style={resolveButtonStyle}
          >
            ✅ Mark Resolved & Send to Cleaning
          </button>

          {ticket.status ===
            "resolved" && (
            <button
              type="button"
              disabled={saving}
              onClick={() =>
                onCloseTicket(ticket)
              }
              style={closeTicketButtonStyle}
            >
              Close Ticket
            </button>
          )}
        </section>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  children,
}) {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>
        {label}
      </span>

      {children}
    </label>
  );
}

function InfoBox({
  label,
  value,
}) {
  return (
    <div style={infoBoxStyle}>
      <span style={infoLabelStyle}>
        {label}
      </span>

      <strong style={infoValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  danger = false,
  warning = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <span style={summaryLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...summaryValueStyle,

          color: danger
            ? "#dc2626"
            : warning
            ? "#c2410c"
            : "#0f172a",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function MetaItem({
  label,
  value,
}) {
  return (
    <div>
      <span style={metaLabelStyle}>
        {label}
      </span>

      <strong style={metaValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function PriorityBadge({
  priority,
}) {
  const config = {
    Low: {
      background: "#e2e8f0",
      color: "#475569",
    },

    Normal: {
      background: "#dbeafe",
      color: "#1d4ed8",
    },

    High: {
      background: "#ffedd5",
      color: "#c2410c",
    },

    Urgent: {
      background: "#fee2e2",
      color: "#b91c1c",
    },
  };

  const selected =
    config[priority] ||
    config.Normal;

  return (
    <span
      style={{
        ...badgeStyle,
        background:
          selected.background,
        color: selected.color,
      }}
    >
      {priority || "Normal"}
    </span>
  );
}

function StatusBadge({
  status,
}) {
  const config = {
    open: {
      background: "#fee2e2",
      color: "#b91c1c",
    },

    assigned: {
      background: "#fef3c7",
      color: "#92400e",
    },

    in_progress: {
      background: "#dbeafe",
      color: "#1d4ed8",
    },

    waiting_parts: {
      background: "#ffedd5",
      color: "#c2410c",
    },

    resolved: {
      background: "#dcfce7",
      color: "#15803d",
    },

    closed: {
      background: "#e2e8f0",
      color: "#475569",
    },
  };

  const selected =
    config[status] ||
    config.open;

  return (
    <span
      style={{
        ...badgeStyle,
        background:
          selected.background,
        color: selected.color,
      }}
    >
      {getStatusLabel(status)}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const sectionStyle = {
  marginTop: 34,
  padding: 18,

  border:
    "1px solid #e2e8f0",

  borderRadius: 18,

  background: "#ffffff",
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 12,
};

const activeBadgeStyle = {
  padding: "8px 12px",

  borderRadius: 999,

  background: "#fee2e2",
  color: "#b91c1c",

  fontWeight: 900,
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(120px,1fr))",

  gap: 9,

  marginTop: 15,
};

const summaryCardStyle = {
  padding: 11,

  border:
    "1px solid #e2e8f0",

  borderRadius: 11,

  background: "#f8fafc",
};

const summaryLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  display: "block",

  marginTop: 4,

  fontSize: 18,
  fontWeight: 900,
};

const toolbarStyle = {
  display: "grid",

  gridTemplateColumns:
    "minmax(230px,1fr) repeat(4,minmax(130px,180px))",

  gap: 9,

  marginTop: 15,
};

const inputStyle = {
  width: "100%",
  minHeight: 42,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 9,

  background: "#ffffff",

  boxSizing: "border-box",

  outline: "none",
};

const errorBoxStyle = {
  marginTop: 13,
  padding: 11,

  border:
    "1px solid #fecaca",

  borderRadius: 10,

  background: "#fef2f2",
  color: "#b91c1c",

  fontSize: 12,
  fontWeight: 700,
};

const emptyBoxStyle = {
  marginTop: 14,
  padding: 18,

  border:
    "1px dashed #cbd5e1",

  borderRadius: 11,

  color: "#64748b",

  textAlign: "center",
};

const ticketGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(270px,1fr))",

  gap: 11,

  marginTop: 15,
};

const ticketCardStyle = {
  padding: 13,

  border:
    "1px solid #e2e8f0",

  borderRadius: 14,

  textAlign: "left",

  cursor: "pointer",

  boxShadow:
    "0 4px 14px rgba(15,23,42,.04)",
};

const ticketTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 10,
};

const roomTitleStyle = {
  color: "#0f172a",

  fontSize: 17,
  fontWeight: 900,
};

const categoryStyle = {
  marginTop: 4,

  color: "#64748b",

  fontSize: 11,
};

const badgeColumnStyle = {
  display: "flex",
  alignItems: "flex-end",
  flexDirection: "column",

  gap: 5,
};

const badgeStyle = {
  display: "inline-block",

  padding: "4px 7px",

  borderRadius: 999,

  fontSize: 9,
  fontWeight: 900,

  whiteSpace: "nowrap",
};

const descriptionStyle = {
  marginTop: 12,

  color: "#334155",

  fontSize: 12,
  lineHeight: 1.5,

  minHeight: 36,
};

const ticketMetaGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(2,minmax(0,1fr))",

  gap: 8,

  marginTop: 12,
  paddingTop: 10,

  borderTop:
    "1px solid #e2e8f0",
};

const metaLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 8,
  fontWeight: 800,

  textTransform: "uppercase",
};

const metaValueStyle = {
  display: "block",

  marginTop: 3,

  color: "#334155",

  fontSize: 10,
  fontWeight: 800,

  overflowWrap: "anywhere",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 28000,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.78)",
};

const modalStyle = {
  width: "min(850px,100%)",
  maxHeight: "96vh",

  overflowY: "auto",

  padding: 20,

  borderRadius: 20,

  background: "#ffffff",

  boxSizing: "border-box",
};

const modalHeaderStyle = {
  position: "sticky",
  top: -20,
  zIndex: 20,

  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,

  margin: "-20px -20px 15px",
  padding: "17px 20px",

  borderBottom:
    "1px solid #e2e8f0",

  background:
    "rgba(255,255,255,.98)",
};

const modalTitleRowStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",
};

const modalTitleStyle = {
  margin: 0,
};

const closeButtonStyle = {
  width: 38,
  height: 38,

  border: 0,
  borderRadius: 10,

  background: "#f1f5f9",

  fontSize: 25,
  cursor: "pointer",
};

const infoGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(150px,1fr))",

  gap: 8,
};

const infoBoxStyle = {
  padding: 9,

  border:
    "1px solid #e2e8f0",

  borderRadius: 9,

  background: "#f8fafc",
};

const infoLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 8,
  fontWeight: 800,

  textTransform: "uppercase",
};

const infoValueStyle = {
  display: "block",

  marginTop: 4,

  color: "#334155",

  fontSize: 10,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const modalSectionStyle = {
  marginTop: 13,
  padding: 13,

  border:
    "1px solid #e2e8f0",

  borderRadius: 13,

  background: "#ffffff",
};

const resolutionSectionStyle = {
  ...modalSectionStyle,

  borderColor: "#bbf7d0",

  background: "#f0fdf4",
};

const modalSectionTitleStyle = {
  margin: "0 0 10px",

  fontSize: 15,
};

const formGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(190px,1fr))",

  gap: 9,
};

const fieldStyle = {
  display: "block",

  marginTop: 9,
};

const fieldLabelStyle = {
  display: "block",

  marginBottom: 6,

  color: "#334155",

  fontSize: 11,
  fontWeight: 800,
};

const saveButtonStyle = {
  width: "100%",

  marginTop: 10,
  padding: 10,

  border: 0,
  borderRadius: 9,

  background: "#475569",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const inlineFormStyle = {
  display: "flex",

  gap: 8,
};

const assignButtonStyle = {
  padding: "10px 15px",

  border: 0,
  borderRadius: 9,

  background: "#2563eb",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const smallInfoStyle = {
  marginTop: 7,

  color: "#64748b",

  fontSize: 10,
};

const actionGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(180px,1fr))",

  gap: 8,
};

const startButtonStyle = {
  minHeight: 42,

  border: 0,
  borderRadius: 9,

  background: "#1d4ed8",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const waitingButtonStyle = {
  minHeight: 42,

  border: 0,
  borderRadius: 9,

  background: "#ea580c",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const resolveButtonStyle = {
  width: "100%",

  marginTop: 10,
  padding: 11,

  border: 0,
  borderRadius: 9,

  background: "#16a34a",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const closeTicketButtonStyle = {
  width: "100%",

  marginTop: 8,
  padding: 10,

  border: 0,
  borderRadius: 9,

  background: "#334155",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

export default MaintenancePanel;